/** Longest edge kept for product photos — enough for kiosks and LINE cards. */
export const PRODUCT_IMAGE_MAX_EDGE = 1024;
/** Mirrors the Worker limit for the encoded upload. */
export const PRODUCT_IMAGE_MAX_BYTES = 2 * 1024 * 1024;
/** Raw phone photos can be large; anything bigger is almost surely not a photo. */
const MAX_SOURCE_BYTES = 25 * 1024 * 1024;

export class ProductImageError extends Error {}

function canvasToBlob(
  canvas: HTMLCanvasElement,
  type: string,
  quality: number,
): Promise<Blob | null> {
  return new Promise((resolve) => canvas.toBlob(resolve, type, quality));
}

/**
 * Downscale and re-encode a picked file before upload: phone photos are
 * several MB, and re-encoding strips EXIF (incl. GPS) metadata. WebP when the
 * browser can encode it, JPEG otherwise (older Safari silently returns PNG).
 */
export async function prepareProductImage(file: File): Promise<Blob> {
  if (!file.type.startsWith('image/')) {
    throw new ProductImageError('กรุณาเลือกไฟล์รูปภาพ');
  }
  if (file.size > MAX_SOURCE_BYTES) {
    throw new ProductImageError('ไฟล์รูปใหญ่เกินไป (เกิน 25 MB)');
  }

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    throw new ProductImageError('เปิดไฟล์รูปนี้ไม่ได้ ลองใช้ JPEG หรือ PNG');
  }
  const scale = Math.min(
    1,
    PRODUCT_IMAGE_MAX_EDGE / Math.max(bitmap.width, bitmap.height),
  );
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  const context = canvas.getContext('2d');
  if (!context) throw new ProductImageError('เบราว์เซอร์นี้ย่อรูปไม่ได้');
  // White matte so transparent PNGs don't turn black as JPEG.
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();

  let blob = await canvasToBlob(canvas, 'image/webp', 0.85);
  if (!blob || blob.type !== 'image/webp') {
    blob = await canvasToBlob(canvas, 'image/jpeg', 0.85);
  }
  if (!blob) throw new ProductImageError('แปลงรูปไม่สำเร็จ');
  if (blob.size > PRODUCT_IMAGE_MAX_BYTES) {
    throw new ProductImageError('รูปยังใหญ่เกิน 2 MB หลังย่อ');
  }
  return blob;
}
